/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `EventPreRegister` for the Caintra Connect frontend.
 * @module pages/EventPreRegister
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/EventPreRegister.module.css";
import saveStyles from "../styles/EventCreation.module.css";

import { useState, useEffect, use, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { Button, CircularProgress, Backdrop } from "@mui/material";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import EventPreRegisterResultModal from "../modals/EventPreRegisterResultModal/index.jsx";
import {
  getGenderOptions,
  getAgeRangeOptions,
  getEventPreRegisterPositionOptions,
  getCompanySizeOptions,
} from "../data/selectOptions";
import {
  getReferencedDataOptionById,
  getSelectedCountryCodeOption,
  getInvoicePhoneDefaultsFromUser,
  resolveRamaLabelFromTree,
  resolveSectorLabelFromTree,
  selectStyles,
} from "../utils/common.js";
import { getUserRoleData } from "../logic/eventsList/index.js";
import {
  getBusinessEvents,
  validatePreRegister,
} from "../logic/businessEvents/index.js";
import { getReferenceData } from "../logic/companyEdit";
import { getSectors } from "../logic/sectors";
import { getUser } from "../logic/user";
import CountryCodeSelector from "../components/CountryCodeSelector";
import {  getCountryCodeOptions  } from "../data/selectOptions";

import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/EventPreRegister/TitleIcon.svg";
import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { canAccessEvents } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

const EVENT_PRE_REGISTER_SELECT_STYLES = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
    borderColor: baseStyles.borderColor,
    marginBottom: "-10px",
    width: "100%",
  }),
  container: (baseStyles) => ({
    ...baseStyles,
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
  }),
  option: (baseStyles) => ({
    ...baseStyles,
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

/** Reference data uses `nombre`, not `label` — required for react-select search/filter. */
const REFERENCE_DATA_SELECT_PROPS = {
  getOptionValue: (option) => option._id,
  getOptionLabel: (option) => option.nombre,
  formatOptionLabel: (option) => <span>{option.nombre}</span>,
};

const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label ?? null;

const resolveOptionValue = (options, apiValue) => {
  if (apiValue === null || apiValue === undefined) {
    return null;
  }
  const byValue = options.find((option) => option.value === Number(apiValue));
  if (byValue) {
    return byValue.value;
  }
  const byLabel = options.find((option) => option.label === apiValue);
  return byLabel?.value ?? null;
};

const getReferenceNombre = (options, id) =>
  getReferencedDataOptionById(options, id)?.nombre ?? null;

const formatReferenceDisplay = (options, apiValue) => {
  if (apiValue === null || apiValue === undefined) {
    return apiValue;
  }
  const byId = getReferencedDataOptionById(options, apiValue);
  if (byId) {
    return byId.nombre;
  }
  return apiValue;
};

const getPhoneDigitsOnly = (phoneValue) =>
  (phoneValue ?? "").replace(/\s/g, "");

const isTenDigitPhone = (phoneValue) =>
  /^\d{10}$/.test(getPhoneDigitsOnly(phoneValue));

const resolvePhonePayload = (
  user,
  isPhoneInputRequired,
  telefonoCodigoPais,
  telefono,
) => {
  if (isPhoneInputRequired) {
    return {
      telefonoCodigoPais,
      telefono: getPhoneDigitsOnly(telefono),
    };
  }

  return getInvoicePhoneDefaultsFromUser(user);
};

const EventPreRegister = () => {
  const { t, i18n } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canAccessEvents);

  const genderOptions = useMemo(() => getGenderOptions(t), [t]);
  const ageRangeOptions = useMemo(() => getAgeRangeOptions(t), [t]);
  const cargoOptions = useMemo(
    () => getEventPreRegisterPositionOptions(t),
    [t],
  );

  const tSpanish = useMemo(() => i18n.getFixedT("es"), [i18n]);
  const spanishGenderOptions = useMemo(
    () => getGenderOptions(tSpanish),
    [tSpanish],
  );
  const spanishAgeRangeOptions = useMemo(
    () => getAgeRangeOptions(tSpanish),
    [tSpanish],
  );
  const spanishCargoOptions = useMemo(
    () => getEventPreRegisterPositionOptions(tSpanish),
    [tSpanish],
  );
  const companySizeOptions = useMemo(() => getCompanySizeOptions(t), [t]);
  const spanishCompanySizeOptions = useMemo(
    () => getCompanySizeOptions(tSpanish),
    [tSpanish],
  );

  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedAgeRange, setSelectedAgeRange] = useState(null);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [formData, setFormData] = useState({
    pais: null,
    estado: null,
    ciudad: null,
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [allTowns, setAllTowns] = useState([]);
  const [filteredTowns, setFilteredTowns] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [user, setUser] = useState(null);
  const [telefonoCodigoPais, setTelefonoCodigoPais] = useState(null);
  const [telefono, setTelefono] = useState("");
  const [referenceDataLoading, setReferenceDataLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isUserRegistered, setIsUserRegistered] = useState(
    location.state?.event?.isUserRegistered === true,
  );
  const [resultModal, setResultModal] = useState(null);
  const phonePrefilledRef = useRef(false);
  const changePermissionsRef = useRef(changePermissions);

  changePermissionsRef.current = changePermissions;

  const isPhoneRequired = String(user?.tipoDeUsuario) === "4";

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const loadPermissionsAndUser = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        return;
      }

      await getUserRoleData(userId, navigate, changePermissionsRef.current);
      const userData = await getUser(userId, navigate);
      if (!userData) {
        return;
      }

      setUser(userData);
      if (
        String(userData.tipoDeUsuario) === "4" &&
        !phonePrefilledRef.current
      ) {
        setTelefonoCodigoPais(userData.telefonoCodigoPais || null);
        setTelefono(userData.telefono || "");
        phonePrefilledRef.current = true;
      }
    };
    loadPermissionsAndUser();
  }, [allowed, navigate]);

  useEffect(() => {
    if (location.state?.event?.isUserRegistered === true) {
      setIsUserRegistered(true);
      return;
    }

    const checkRegistrationStatus = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId || !eventId || !user) {
        return;
      }

      const events = await getBusinessEvents(userId, user, navigate);
      const event = events.find((item) => item._id === eventId);
      if (event?.isUserRegistered) {
        setIsUserRegistered(true);
      }
    };

    checkRegistrationStatus();
  }, [user, eventId, navigate, location.state?.event?.isUserRegistered]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setReferenceDataLoading(true);
        const [countriesData, statesData, townsData, sectorsData] =
          await Promise.all([
            getReferenceData("countries", navigate),
            getReferenceData("states", navigate),
            getReferenceData("towns", navigate),
            getSectors(navigate),
          ]);

        const loadedCountries =
          countriesData && countriesData.length > 0 ? countriesData : [];
        const loadedStates =
          statesData && statesData.length > 0 ? statesData : [];
        const loadedTowns = townsData && townsData.length > 0 ? townsData : [];
        const loadedSectors = Array.isArray(sectorsData) ? sectorsData : [];

        setCountries(loadedCountries);
        setStates(loadedStates);
        setAllTowns(loadedTowns);
        setFilteredTowns(loadedTowns);
        setSectors(loadedSectors);
      } catch (error) {
        logger.error("Error loading location reference data:", {
          err: sanitizeError(error),
        });
        setCountries([]);
        setStates([]);
        setAllTowns([]);
        setFilteredTowns([]);
        setSectors([]);
      } finally {
        setReferenceDataLoading(false);
      }
    };

    if (!allowed) {
      return;
    }
    loadReferenceData();
  }, [allowed, navigate]);

  useEffect(() => {
    setSelectedGender((prev) =>
      prev == null
        ? null
        : (genderOptions.find((option) => option.value === prev.value) ?? null),
    );
  }, [genderOptions]);

  useEffect(() => {
    setSelectedAgeRange((prev) =>
      prev == null
        ? null
        : (ageRangeOptions.find((option) => option.value === prev.value) ??
          null),
    );
  }, [ageRangeOptions]);

  useEffect(() => {
    setSelectedCargo((prev) =>
      prev == null
        ? null
        : (cargoOptions.find((option) => option.value === prev.value) ?? null),
    );
  }, [cargoOptions]);

  const isPhoneValid =
    !isPhoneRequired ||
    (telefonoCodigoPais != null &&
      String(telefonoCodigoPais).trim() !== "" &&
      isTenDigitPhone(telefono));

  const showPhoneError =
    isPhoneRequired && telefono.trim() !== "" && !isTenDigitPhone(telefono);

  const handleTelefonoChange = (value) => {
    if (value !== "" && !/^[\d\s]*$/.test(value)) {
      return;
    }
    setTelefono(value);
  };

  const isFormValid =
    selectedGender != null &&
    selectedAgeRange != null &&
    selectedCargo != null &&
    formData.pais != null &&
    formData.estado != null &&
    formData.ciudad != null &&
    isPhoneValid;

  const handleBack = () => {
    navigate("/events", {
      state: isUserRegistered ? { preRegisteredEventId: eventId } : undefined,
    });
  };

  const handleCloseResultModal = () => {
    setResultModal(null);
  };

  const formatDisplayData = (apiData) => {
    const generoValue = resolveOptionValue(
      spanishGenderOptions,
      apiData.genero,
    );
    const rangoEdadValue = resolveOptionValue(
      spanishAgeRangeOptions,
      apiData.rangoEdad,
    );
    const cargoValue = resolveOptionValue(spanishCargoOptions, apiData.cargo);

    const sectorRaw = apiData.sector ?? apiData.sectorEconomico;
    const giroRaw = apiData.giro ?? apiData.ramaSCIAN;
    const tamanoRaw =
      apiData.tamanoEmpresa ?? apiData.tamanoDeLaEmpresa ?? null;

    const tamanoValue =
      tamanoRaw == null
        ? null
        : resolveOptionValue(spanishCompanySizeOptions, tamanoRaw);

    const displayData = {
      tipoVisitante: apiData.tipoVisitante,
      nombre: apiData.nombre,
      apellido1: apiData.apellido1,
      apellido2: apiData.apellido2,
      correoElectronico: apiData.correoElectronico,
      numeroCelular: apiData.numeroCelular,
      genero: getOptionLabel(genderOptions, generoValue) ?? apiData.genero,
      rangoEdad:
        getOptionLabel(ageRangeOptions, rangoEdadValue) ?? apiData.rangoEdad,
      cargo: getOptionLabel(cargoOptions, cargoValue) ?? apiData.cargo,
      rfcEmpresa: apiData.rfcEmpresa ?? apiData.RFC,
      nombreComercialEmpresa:
        apiData.nombreComercialEmpresa ?? apiData.nombreComercial,
      razonSocialEmpresa: apiData.razonSocialEmpresa ?? apiData.razonSocial,
      eresSocioCaintra:
        apiData.eresSocioCaintra === true
          ? t("common.yes")
          : apiData.eresSocioCaintra === false
            ? t("common.no")
            : apiData.eresSocioCaintra,
      numeroSocio: apiData.numeroSocio,
      sector: resolveSectorLabelFromTree(sectors, sectorRaw),
      giro: resolveRamaLabelFromTree(sectors, giroRaw),
      tamanoEmpresa:
        tamanoValue != null
          ? (getOptionLabel(companySizeOptions, tamanoValue) ?? tamanoRaw)
          : tamanoRaw,
      cantidadPersonalOcupado: apiData.cantidadPersonalOcupado,
      pais: formatReferenceDisplay(countries, apiData.pais),
      estado: formatReferenceDisplay(states, apiData.estado),
      ciudad: formatReferenceDisplay(allTowns, apiData.ciudad),
      interesado: apiData.interesado,
      asistePor: apiData.asistePor,
      seEntero: apiData.seEntero,
    };

    return displayData;
  };

  const handleRegister = async () => {
    if (!isFormValid || submitting) {
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }

    setSubmitting(true);

    try {
      const phonePayload = resolvePhonePayload(
        user,
        isPhoneRequired,
        telefonoCodigoPais,
        telefono,
      );

      const formPayload = {
        genero: getOptionLabel(spanishGenderOptions, selectedGender?.value),
        rangoEdad: getOptionLabel(
          spanishAgeRangeOptions,
          selectedAgeRange?.value,
        ),
        cargo: getOptionLabel(spanishCargoOptions, selectedCargo?.value),
        pais: getReferenceNombre(countries, formData.pais),
        estado: getReferenceNombre(states, formData.estado),
        ciudad: getReferenceNombre(allTowns, formData.ciudad),
        telefonoCodigoPais: phonePayload.telefonoCodigoPais,
        telefono: phonePayload.telefono,
      };

      const result = await validatePreRegister(
        eventId,
        userId,
        formPayload,
        navigate,
      );

      if (!result) {
        return;
      }

      if (result.success) {
        setIsUserRegistered(true);
        setResultModal({
          variant: "success",
          message: t("pages.eventPreRegister.validationSuccess"),
          data: formatDisplayData(result.data),
        });
        return;
      }

      if (result.alreadyRegistered) {
        setIsUserRegistered(true);
        setResultModal({
          variant: "error",
          message: t("pages.eventPreRegister.alreadyRegistered"),
        });
        return;
      }

      if (
        result.status === 422 &&
        Array.isArray(result.fieldsMissing) &&
        result.fieldsMissing.length > 0
      ) {
        setResultModal({
          variant: "error",
          message: t("pages.eventPreRegister.profileIncomplete"),
          data: { fieldsMissing: result.fieldsMissing },
        });
        return;
      }

      setResultModal({
        variant: "error",
        message:
          result.message ||
          (result.status === 422
            ? t("pages.eventPreRegister.profileIncomplete")
            : t("pages.eventPreRegister.submitError")),
        data: result.fieldsMissing?.length
          ? { fieldsMissing: result.fieldsMissing }
          : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <EventPreRegisterResultModal
        open={resultModal != null}
        onClose={handleCloseResultModal}
        variant={resultModal?.variant}
        message={resultModal?.message}
        data={resultModal?.data}
      />

      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              type="button"
              className={styles.returnButtonContainer}
              onClick={handleBack}
            >
              <ChevronLeftIcon />
              <span>{t("common.goBack")}</span>
            </button>
            <div className={styles.titleContainer}>
              <TitleIcon />
              <h1 className={styles.title}>
                {t("pages.eventPreRegister.header")}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formContentContainer}>
        <div className={styles.sectionContent}>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.gender")}
              </label>
              <Select
                isSearchable
                value={selectedGender}
                options={genderOptions}
                onChange={(selectedOption) => {
                  setSelectedGender(selectedOption ?? null);
                }}
                placeholder={t("pages.eventPreRegister.genderPlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                getOptionValue={(option) => option.value}
                formatOptionLabel={(option) => <span>{option.label}</span>}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.ageRange")}
              </label>
              <Select
                isSearchable
                value={selectedAgeRange}
                options={ageRangeOptions}
                onChange={(selectedOption) => {
                  setSelectedAgeRange(selectedOption ?? null);
                }}
                placeholder={t("pages.eventPreRegister.ageRangePlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                getOptionValue={(option) => option.value}
                formatOptionLabel={(option) => <span>{option.label}</span>}
              />
            </div>

            <div className={`${styles.inputGroup} ${styles.cargoGroup}`}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.position")}
              </label>
              <Select
                isSearchable
                value={selectedCargo}
                options={cargoOptions}
                onChange={(selectedOption) => {
                  setSelectedCargo(selectedOption ?? null);
                }}
                placeholder={t("pages.eventPreRegister.positionPlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                getOptionValue={(option) => option.value}
                formatOptionLabel={(option) => <span>{option.label}</span>}
              />
            </div>
          </div>

          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.country")}
              </label>
              <Select
                isSearchable
                value={getReferencedDataOptionById(countries, formData.pais)}
                options={countries}
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    pais: selectedOption?._id ?? null,
                    estado: null,
                    ciudad: null,
                  }));
                  setFilteredTowns(allTowns);
                }}
                placeholder={t("pages.eventPreRegister.countryPlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                {...REFERENCE_DATA_SELECT_PROPS}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.state")}
              </label>
              <Select
                isSearchable
                value={getReferencedDataOptionById(states, formData.estado)}
                options={states}
                onChange={(selectedOption) => {
                  const stateId = selectedOption?._id ?? null;
                  setFormData((prev) => ({
                    ...prev,
                    estado: stateId,
                    ciudad: null,
                  }));
                  setFilteredTowns(
                    stateId
                      ? allTowns.filter((town) => town.idEstado == stateId)
                      : allTowns,
                  );
                }}
                placeholder={t("pages.eventPreRegister.statePlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                {...REFERENCE_DATA_SELECT_PROPS}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                *{t("pages.eventPreRegister.city")}
              </label>
              <Select
                isSearchable
                isDisabled={
                  formData.estado == null || filteredTowns.length === 0
                }
                value={getReferencedDataOptionById(
                  filteredTowns,
                  formData.ciudad,
                )}
                options={filteredTowns}
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    ciudad: selectedOption?._id ?? null,
                  }));
                }}
                placeholder={t("pages.eventPreRegister.cityPlaceholder")}
                styles={EVENT_PRE_REGISTER_SELECT_STYLES}
                className="select__control"
                {...REFERENCE_DATA_SELECT_PROPS}
              />
            </div>
          </div>

          {isPhoneRequired && (
            <div className={styles.inputRow}>
              <div className={`${styles.inputGroup} ${styles.phoneGroup}`}>
                <label
                  className={styles.inputLabel}
                  htmlFor="event-pre-register-telefono"
                >
                  *{t("pages.eventPreRegister.phone")}
                </label>
                <div className={styles.phoneInputContainer}>
                  <CountryCodeSelector
                    value={getSelectedCountryCodeOption(
                      countryCodeOptions,
                      telefonoCodigoPais,
                    )}
                    onChange={(selectedOption) => {
                      setTelefonoCodigoPais(selectedOption?.value ?? null);
                    }}
                    styles={selectStyles}
                    placeholder={t("common.select")}
                  />
                  <input
                    id="event-pre-register-telefono"
                    type="text"
                    className={`${styles.textInput} ${
                      showPhoneError ? styles.textInputError : ""
                    }`}
                    placeholder={t("pages.eventPreRegister.phonePlaceholder")}
                    value={telefono}
                    onChange={(event) =>
                      handleTelefonoChange(event.target.value)
                    }
                    aria-invalid={showPhoneError}
                    aria-describedby={
                      showPhoneError
                        ? "event-pre-register-telefono-error"
                        : undefined
                    }
                  />
                </div>
                {showPhoneError && (
                  <p
                    id="event-pre-register-telefono-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {t("errors.validation.PHONE_MUST_BE_10_DIGITS")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`${saveStyles.saveSectionContainer} ${
          resultModal != null ? styles.saveSectionBehindModal : ""
        }`}
        aria-hidden={resultModal != null}
      >
        <div className={saveStyles.saveSectionContent}>
          <div className={saveStyles.messageContainer}>
            {t("pages.eventPreRegister.registerInstructions")}
          </div>
          <Button
            type="button"
            variant="contained"
            className={saveStyles.saveButton}
            disabled={
              !isFormValid ||
              submitting ||
              referenceDataLoading ||
              isUserRegistered
            }
            onClick={handleRegister}
            startIcon={submitting || isUserRegistered ? null : <SaveIcon />}
          >
            {submitting ? (
              <span className={styles.saveButtonSpinner}>
                <CircularProgress
                  size={24}
                  thickness={4}
                  color="inherit"
                  disableShrink
                />
              </span>
            ) : isUserRegistered ? (
              t("pages.eventPreRegister.registered")
            ) : (
              t("pages.eventPreRegister.register")
            )}
          </Button>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={referenceDataLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default EventPreRegister;
